// Pure helpers for the LINK STRUCTURE embedded in a harness memory store —
// zero dependencies, so node tests and the markdown renderer can share them
// (mirrors memoryNames.js: pure slug logic, no analytics/DOM imports).
//
// A memory store is a small graph the harness maintains about the user:
//   • MEMORY.md (the index) points at each fact with a markdown link:
//       - [Origin product direction shift](project_product_direction.md) — hook
//   • leaf facts cross-reference each other with wikilinks:  [[other_slug]]
//
// Both forms name another memory by its SLUG — the `<slug>.md` filename for a
// markdown pointer, or the bare slug inside a wikilink. The dashboard already
// discovers which memory files were actually captured (memories.js, sliced from
// PROMPT_RESOURCE_PATHS); these helpers expose the link targets so the UI can
// resolve them against that captured set and make the graph traversable —
// turning an index that merely MENTIONS a file into one you can click into, and
// surfacing the referenced-but-never-captured files instead of silently
// dropping them (the gap that makes the index look broken).

// Pull the memory SLUG out of a markdown link href, or null when the href is
// not a memory pointer. A memory pointer is a BARE `<slug>.md` filename — no
// scheme, no path separators, no query/anchor. Real URLs in a memory body
// (https://…, mailto:, #anchors, ../paths) are NOT memory links and must keep
// rendering as ordinary external links.
export function memorySlugFromHref(href) {
  const h = String(href ?? "").trim();
  if (!h) return null;
  // Any scheme / path / query / anchor disqualifies it as a bare filename.
  if (/[/\\:?#]/.test(h)) return null;
  const m = /^([A-Za-z0-9][A-Za-z0-9._-]*)\.md$/.exec(h);
  if (!m) return null;
  const slug = m[1];
  // Final guard mirrors extractMemoryFromPath: must carry an alnum char.
  if (!/[a-z0-9]/i.test(slug)) return null;
  return slug;
}

// Normalize a wikilink target (`[[slug]]` or the `slug` half of `[[slug|Display]]`)
// to a bare slug, or null when it doesn't look like one. Tolerates a trailing
// `.md` (some authors write `[[foo.md]]`) and rejects anything path-like.
export function slugFromWikiTarget(raw) {
  const s = String(raw ?? "").trim().replace(/\.md$/i, "");
  if (!s) return null;
  if (/[/\\:?#[\]|]/.test(s)) return null;
  if (!/[a-z0-9]/i.test(s)) return null;
  return s;
}

// Every memory slug this body references — via markdown pointers `[..](slug.md)`
// and wikilinks `[[slug]]` — deduped, in first-seen order. Feeds the coverage
// summary ("references N memories · M captured · K uncaptured"); inline
// rendering resolves each link separately.
export function extractMemoryRefs(text) {
  const out = [];
  const seen = new Set();
  const push = (slug) => {
    if (slug && !seen.has(slug)) {
      seen.add(slug);
      out.push(slug);
    }
  };
  const body = String(text ?? "");
  // Markdown pointers: [label](href)
  const mdRe = /\[[^\]]*\]\(([^)]+)\)/g;
  let m;
  while ((m = mdRe.exec(body))) push(memorySlugFromHref(m[1]));
  // Wikilinks: [[slug]] or [[slug|Display]]
  const wikiRe = /\[\[([^\]|]+)(?:\|[^\]]*)?\]\]/g;
  while ((m = wikiRe.exec(body))) push(slugFromWikiTarget(m[1]));
  return out;
}

// Split a leading YAML frontmatter block (`---\n…\n---`) from the markdown body.
// Memory leaf files carry frontmatter (name/description/metadata); the index
// (MEMORY.md) does not. Returns { frontmatter, body } — frontmatter null when
// absent, so the index renders unchanged and a leaf file's metadata can be shown
// distinctly from its prose.
export function splitFrontmatter(text) {
  const s = String(text ?? "");
  const m = /^﻿?---\n([\s\S]*?)\n---[ \t]*\n?/.exec(s);
  if (!m) return { frontmatter: null, body: s };
  return { frontmatter: m[1], body: s.slice(m[0].length) };
}
